<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ updateMode ? $t('tsResponderUpdate') : $t('createTsResponder') }}
        </v-card-title>
        <v-list-item>
          <v-list-item-content>
            <v-form ref="form" v-model="formValid">
              <v-row align="center">
                <v-col cols="6">
                  <v-text-field
                    id="30ab03d1"
                    v-model="tsResponder.name"
                    :label="$t('name')+`*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                    :disabled="updateMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    id="8b5742ef"
                    v-model="tsResponder.policyOid"
                    :label="$t('policyOid')+`*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div v-if="!updateMode" class="text-center d-flex align-center">
                <v-text-field
                  id="235de497"
                  v-model="tsResponder.subjectDn"
                  :label="$t('subjectDn')+`*`"
                  :counter="maxCharacters"
                  :rules="[rules.required, rules.minMax]"
                  :disabled="updateMode"
                ></v-text-field>
                <v-tooltip v-if="!updateMode" top>
                  <template v-slot:activator="{ on }">
                    <v-icon color="primary" v-on="on">mdi-help-circle</v-icon>
                  </template>
                  <span>{{ $t('helper.subjectDn') }}</span>
                </v-tooltip>
              </div>

              <v-row align="center">
                <v-col cols="10" md="2">
                  <v-switch
                    id="a1ba360f"
                    v-model="tsResponder.qualified"
                    class="mx-3"
                    :label="$t('qualified')"
                    :disabled="updateMode"
                  ></v-switch>
                </v-col>
                <v-col id="728cab06" cols="6" md="5">
                  <v-select
                    v-model="tsResponder.acceptedDigestAlgs"
                    class="mx-3"
                    :items="acceptedDigestAlgList"
                    :label="$t('select') + ' ' + $t('acceptedDigestAlgorithms')+`*`"
                    :rules="[rules.atLeastOneItem]"
                    multiple
                    chips
                    validate-on-blur
                  ></v-select>
                </v-col>
                <v-col cols="6" md="3">
                  <v-select
                    id="7ada0f19"
                    v-model="tsResponder.digestAlg"
                    class="mx-3"
                    :items="digestAlgorithmList"
                    :label="$t('select') + ' ' + $t('digestAlgorithm')"
                    chips
                  ></v-select>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col cols="6" md="4">
                  <v-select
                    id="f5400529"
                    v-model="tsResponder.keyStoreName"
                    class="mx-3"
                    :items="keyStoreNameList"
                    :label="$t('select') + ' ' + $t('keyStoreName')+`*`"
                    :rules="[rules.required]"
                    chips
                    :disabled="updateMode"
                    @change="keyStoreNameChanged"
                  ></v-select>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select
                    id="e910a4a7"
                    v-model="tsResponder.serialNumType"
                    class="mx-3"
                    :items="serialNumTypeList"
                    :label="$t('select') + ' ' + $t('serialNumType')"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select
                    id="b46bd0ec"
                    v-model="tsResponder.resolution"
                    class="mx-3"
                    :items="resolutionList"
                    :label="$t('select') + ' ' + $t('resolution')"
                    chips
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="!updateMode" align="center">
                <v-col>
                  <v-radio-group
                    id="14d28707"
                    v-model="keyMethod"
                    class="mx-3"
                    row
                    @change="changeVisibleKeyMethodPlatform"
                  >
                    <v-radio
                      :label="$t('keyMethod.generate')"
                      :value="keyMethodList[0]"
                    ></v-radio>
                    <v-radio
                      :label="$t('keyMethod.import')"
                      :value="keyMethodList[1]"
                    ></v-radio>
                    <v-radio
                      :label="$t('keyMethod.exist')"
                      :value="keyMethodList[2]"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row v-if="keyGenerationPlatform" align="center">
                <v-col cols="6" md="2">
                  <v-select
                    id="b2ea103d"
                    v-model="tsResponder.generateKey.keyAlg"
                    class="mx-3"
                    :items="keyAlgList"
                    :label="$t('select') + ' ' + $t('keyAlg')"
                    chips
                    :disabled="updateMode"
                    @change="changeDefaultValuesByKeyAlg"
                  ></v-select>
                </v-col>
                <v-col cols="6" md="2">
                  <v-select
                    id="b27bde12"
                    v-model="tsResponder.generateKey.keySize"
                    class="mx-3"
                    :items="tsResponder.generateKey.keyAlg === keyAlgList[1] ? ecKeyNameList : rsaKeySizeList"
                    :label="$t('select') + ' ' + $t('keySize')"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="keyImportPlatform">
                <v-col cols="6" md="1">
                  <input
                    id="0df6ea98"
                    ref="keyUpload"
                    style="display: none"
                    type="file"
                    @change="loadKeyFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.keyUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ !!tsResponder.importKey.pem ? $t('replace') : $t('upload') }}
                  </v-btn>
                </v-col>
                <v-col cols="6" md="4">
                  <v-text-field
                    id="32a39a3a"
                    v-model="tsResponder.importKey.password"
                    :label="$t('keyPassword')"
                    type="password"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="existingKeyPlatform">
                <v-col>
                  <v-select
                    id="ec295f3f"
                    v-model="tsResponder.keyAlias"
                    class="mx-3"
                    :items="keyAliasList"
                    item-text="text"
                    item-value="value"
                    :label="$t('select') + ' ' + $t('keyAlias')"
                    :rules="[rules.required]"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col cols="2">
                  <v-switch
                    id="cf113430"
                    v-model="setAccuracy"
                    class="mx-3"
                    :label="$t('accuracy')"
                  ></v-switch>
                </v-col>
                <v-col v-if="setAccuracy" cols="2">
                  <v-row align="center" class="mx-2">
                    <v-checkbox
                      id="819c492a"
                      v-model="accuracySecondsOptional"
                      hide-details
                      class="shrink mr-2 mt-0"
                    ></v-checkbox>
                    <v-text-field
                      id="6d2a003c"
                      v-model="tsResponder.accuracySeconds"
                      :rules="[rules.required]"
                      :disabled="!accuracySecondsOptional"
                      :label="$t('accuracySeconds')+`*`"
                      min="0"
                      type="number"
                    ></v-text-field>
                  </v-row>
                </v-col>
                <v-col v-if="setAccuracy" cols="2">
                  <v-row align="center" class="mx-2">
                    <v-checkbox
                      id="70d7ad49"
                      v-model="accuracyMillisOptional"
                      hide-details
                      class="shrink mr-2 mt-0"
                    ></v-checkbox>
                    <v-text-field
                      id="5a497686"
                      v-model="tsResponder.accuracyMillis"
                      :rules="[rules.required, rules.numberFromToValid]"
                      :disabled="!accuracyMillisOptional"
                      :label="$t('accuracyMillis')+`*`"
                      min="1"
                      max="999"
                      type="number"
                    ></v-text-field>
                  </v-row>
                </v-col>
                <v-col v-if="setAccuracy" cols="3">
                  <v-row align="center" class="mx-2">
                    <v-checkbox
                      id="75179134"
                      v-model="accuracyMicrosOptional"
                      hide-details
                      class="shrink mr-2 mt-0"
                    ></v-checkbox>
                    <v-text-field
                      id="bcb0c4ea"
                      v-model="tsResponder.accuracyMicros"
                      :rules="[rules.required, rules.numberFromToValid]"
                      :disabled="!accuracyMicrosOptional"
                      :label="$t('accuracyMicros')+`*`"
                      min="1"
                      max="999"
                      type="number"
                    ></v-text-field>
                  </v-row>
                </v-col>
              </v-row>

              <!-- downloadCertReq -->
              <v-btn
                v-if="updateMode"
                id="c111b401"
                class="primary mx-2 my-2"
                @click="downloadCertReqFile(tsResponder.certReq.trim())"
              >
                <v-icon left>mdi-download</v-icon>
                {{ $t('download') + ' ' + $t('certReq') }}
              </v-btn>

              <!-- loadSignerCertificate -->
              <div v-if="updateMode" class="mx-2">
                <input
                  id="e6344c73"
                  ref="signerCertificateUpload"
                  style="display: none"
                  type="file"
                  accept=".pem, .cer"
                  @change="loadSignerCertificateFromFile($event)"
                >
                <v-btn class="green" @click="$refs.signerCertificateUpload.click()">
                  <v-icon left>mdi-upload</v-icon>
                  {{ !!tsResponder.signerCert ? $t('replace') : $t('upload') }} {{ $t('signerCert') }}
                </v-btn>
              </div>

              <!-- signerCert -->
              <v-row v-if="updateMode && !!tsResponder.signerCert" id="be2622e3">
                <v-col>
                  <v-card class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('signerCert') }}
                        </v-list-item-subtitle>
                        <v-text-field
                          v-model="tsResponder.signerCert.serialNumber"
                          :label="$t('serialNumber')"
                          :readonly="!!tsResponder.signerCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="tsResponder.signerCert.issuer"
                          :label="$t('issuer')"
                          :readonly="!!tsResponder.signerCert"
                        ></v-text-field>
                        <v-text-field
                          v-model="tsResponder.signerCert.subject"
                          :label="$t('subject')"
                          :readonly="!!tsResponder.signerCert"
                        ></v-text-field>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(tsResponder.signerCert.notBefore)"
                              :label="$t('notBefore')"
                              :readonly="!!tsResponder.signerCert"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(tsResponder.signerCert.notAfter)"
                              :label="$t('notAfter')"
                              :readonly="!!tsResponder.signerCert"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <!-- loadAdditionalCertificate -->
              <div v-if="updateMode" class="mx-2 my-2">
                <input
                  id="9ac81554"
                  ref="additionalCertificateUpload"
                  style="display: none"
                  type="file"
                  accept=".pem, .cer"
                  @change="loadAdditionalCertificateFromFile($event)"
                >
                <v-btn class="green" @click="$refs.additionalCertificateUpload.click()">
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('upload') + ' ' + $t('additionalCert') }}
                </v-btn>
              </div>

              <!-- additionalCert(s) -->
              <v-row v-if="updateMode" id="f2a2625e">
                <v-col v-for="(cert, index) in tsResponder.additionalCerts" :key="index" cols="12">
                  <v-card :id="'f2a2625' + index" class="mx-2" raised>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle class="subtitle-2 text-left">
                          {{ $t('additionalCert') + ' - ' + (index + 1) }}
                        </v-list-item-subtitle>
                        <v-text-field
                          v-model="tsResponder.additionalCerts[index].serialNumber"
                          :label="$t('serialNumber')" :readonly="!!tsResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-text-field
                          v-model="tsResponder.additionalCerts[index].issuer"
                          :label="$t('issuer')"
                          :readonly="!!tsResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-text-field
                          v-model="tsResponder.additionalCerts[index].subject"
                          :label="$t('subject')"
                          :readonly="!!tsResponder.additionalCerts[index]"
                        ></v-text-field>
                        <v-row>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(tsResponder.additionalCerts[index].notBefore)"
                              :label="$t('notBefore')"
                              :readonly="!!tsResponder.additionalCerts[index]"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field
                              :value="convertDate(tsResponder.additionalCerts[index].notAfter)"
                              :label="$t('notAfter')"
                              :readonly="!!tsResponder.additionalCerts[index]"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                    <v-card-actions>
                      <v-btn color="error" @click="removeAdditionalCert(index)">{{ $t('delete') }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-list-item-content>
        </v-list-item>
        <v-card-actions>
          <v-btn
            id="2a73b2be"
            class="ma-2"
            color="primary"
            :loading="btnLoading"
            :disabled="!formValid || btnLoading"
            @click="createOrUpdateTsResponder"
          >
            {{ updateMode ? $t('update') : $t('create') }}
          </v-btn>
          <v-btn id="5110ad04" class="ma-2" @click="back">{{ $t('back') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import keystores from '@/api/keystores'
import signers from '@/api/signers'
import certificates from '@/api/certificates'
import FileSaver from 'file-saver'
import responderResolution from './ResponderResolution'
import serialNumType from './SerialNumType'
import messageDigest from './MessageDigest'
import keyMethod from './KeyMethod'
import keyAlgorithm from './KeyAlgorithm'
import rsaKeySize from './RsaKeySize'
import ecKeyName from './EcKeyName'
import digestAlgorithm from './DigestAlgorithm'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'CreateTsResponderOrUpdate',

  data() {
    return {
      formValid: true,
      maxCharacters: 255,
      keyStoreNameList: [],
      acceptedDigestAlgList: messageDigest,
      serialNumTypeList: serialNumType,
      resolutionList: responderResolution,
      keyMethodList: keyMethod,
      keyMethod: keyMethod[0],
      keyAliasList: [],
      keyAlgList: keyAlgorithm,
      rsaKeySizeList: rsaKeySize,
      ecKeyNameList: ecKeyName,
      digestAlgorithmList: digestAlgorithm,
      setAccuracy: false,
      accuracySecondsOptional: false,
      accuracyMillisOptional: false,
      accuracyMicrosOptional: false,
      tsResponder: {
        qualified: true,
        acceptedDigestAlgs: [messageDigest[1], messageDigest[3]],
        keyStoreName: null,
        serialNumType: serialNumType[1],
        resolution: responderResolution[2],
        digestAlg: digestAlgorithm[1],
        generateKey: {
          keyAlg: keyAlgorithm[0],
          keySize: rsaKeySize[0]
        },
        importKey: {
          pem: null,
          password: null
        },
        keyAlias: null,
        accuracySeconds: 1,
        accuracyMillis: 1,
        accuracyMicros: 1
      },
      keyGenerationPlatform: true,
      keyImportPlatform: false,
      existingKeyPlatform: false,
      btnLoading: false,
      rules: {
        required: v => !!v || this.$t('validation.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.mustBeLessThan', { max: this.maxCharacters }),
        atLeastOneItem: v => (v && v.length > 0) || this.$t('validation.atLeastOneItem'),
        numberFromToValid: v => (v >= 1 && v <= 999) || this.$t('validation.numberFromToValid', { min: 1, max: 999 })
      }
    }
  },

  computed: {
    updateMode() {
      return this.$route.params.tsResponderId != null
    }
  },

  async mounted() {
    if (this.updateMode) {
      await this.getTsResponder()
    }
    await this.getKeyStoreNames()
  },

  methods: {
    async getTsResponder() {
      try {
        this.tsResponder = await signers.getTsResponder(this.$route.params.tsResponderId)
        this.setKeyMethodPlatformValues()
        this.setDefaultAccuracyValuesByGetTsResponder()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    async createOrUpdateTsResponder() {
      this.btnLoading = true

      if (!this.$refs.form.validate()) {
        return
      }

      let tsResponderDto = Object.assign({}, this.tsResponder, {
        generateKey: this.keyMethod === this.keyMethodList[0] ? this.tsResponder.generateKey : null,
        importKey: this.keyMethod === this.keyMethodList[1] ? this.tsResponder.importKey : null,
        keyAlias: this.keyMethod === this.keyMethodList[2] ? this.tsResponder.keyAlias : null,
        accuracySeconds: this.accuracySecondsOptional ? this.tsResponder.accuracySeconds : null,
        accuracyMillis: this.accuracyMillisOptional ? this.tsResponder.accuracyMillis : null,
        accuracyMicros: this.accuracyMicrosOptional ? this.tsResponder.accuracyMicros : null
      })

      try {
        this.updateMode ? await signers.updateTsResponder(this.$route.params.tsResponderId, tsResponderDto) : await signers.createTsResponder(tsResponderDto)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'ALREADY_EXISTS':
              errorMsg = this.$t('toastMessage.alreadyExist')
              break
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      } finally {
        this.btnLoading = false
      }
    },

    async getKeyStoreNames() {
      try {
        this.keyStoreNameList = await keystores.getKeyStoreNames()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    changeDefaultValuesByKeyAlg() {
      this.tsResponder.generateKey.keySize = (this.tsResponder.generateKey.keyAlg === this.keyAlgList[1] ? this.ecKeyNameList[0] : this.rsaKeySizeList[0])
    },

    changeVisibleKeyMethodPlatform() {
      switch (this.keyMethod) {
        case this.keyMethodList[0]:
          this.configurationOfVisiblePlatforms(true, false, false)
          break
        case this.keyMethodList[1]:
          this.configurationOfVisiblePlatforms(false, true, false)
          break
        case this.keyMethodList[2]:
          this.configurationOfVisiblePlatforms(false, false, true)
          break
      }
    },

    configurationOfVisiblePlatforms(keyGen, keyImp, keyExist) {
      this.keyGenerationPlatform = keyGen
      this.keyImportPlatform = keyImp
      this.existingKeyPlatform = keyExist
    },

    setKeyMethodPlatformValues() {
      const isGenerateKeyEmpty = Object.keys(this.tsResponder.generateKey).length === 0
      this.keyGenerationPlatform = !isGenerateKeyEmpty
      if (isGenerateKeyEmpty) {
        this.keyAliasList = [{ text: this.tsResponder.keyAlias, value: this.tsResponder.keyAlias }]
        this.existingKeyPlatform = this.tsResponder.keyAlias !== null
      }
    },

    async downloadCertReqFile(data) {
      await FileSaver.saveAs(new Blob([data], { type: 'application/x-pem-file' }), this.tsResponder.name + '-certReq.pem')
    },

    loadKeyFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => { this.tsResponder.importKey.pem = event.target.result }
      reader.readAsText(file)
      this.$refs.keyUpload.value = null
    },

    loadSignerCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addSignerCert(event.target.result)
      reader.readAsText(file)
      this.$refs.signerCertificateUpload.value = null
    },

    async addSignerCert(value) {
      this.$set(this.tsResponder, 'signerCert', Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    loadAdditionalCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addAdditionalCert(event.target.result)
      reader.readAsText(file)
      this.$refs.additionalCertificateUpload.value = null
    },

    async addAdditionalCert(value) {
      this.tsResponder.additionalCerts.push(Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    removeAdditionalCert(index) {
      this.tsResponder.additionalCerts.splice(index, 1)
    },

    async parseCertificate(pem) {
      try {
        return await certificates.parseCertificate(pem)
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
        throw error
      }
    },

    async keyStoreNameChanged() {
      const entries = await keystores.getKeyStoreEntries(this.tsResponder.keyStoreName)
      this.keyAliasList = entries.flatMap(e => {
        return { text: `${e.keyAlias} (${e.keyIdentifier})`, value: e.keyAlias }
      })
    },

    setDefaultAccuracyValuesByGetTsResponder() {
      // accuracy value if already exist when checkbox true
      this.accuracySecondsOptional = !!this.tsResponder.accuracySeconds
      this.accuracyMillisOptional = !!this.tsResponder.accuracyMillis
      this.accuracyMicrosOptional = !!this.tsResponder.accuracyMicros

      // main accuracy toggle status, visible accuracy options or not
      this.setAccuracy = this.accuracySecondsOptional || this.accuracyMillisOptional || this.accuracyMicrosOptional

      // if not already exist when set default value
      if (!this.accuracySecondsOptional) {
        this.tsResponder.accuracySeconds = 1
      }
      if (!this.accuracyMillisOptional) {
        this.tsResponder.accuracyMillis = 1
      }
      if (!this.accuracyMicrosOptional) {
        this.tsResponder.accuracyMicros = 1
      }
    },

    back() {
      this.$router.go(-1)
    }
  }
}
</script>
